/**
 * A room's Follow-ups tab: the automations that run on it, drawn as the Automations tab draws them, each with a
 * switch for this room alone, and the invites it sent.
 */
import { useNavigate } from "react-router";
import type { AutomationSummary, InviteSendView, WebinarDetail } from "../../../shared/types";
import { FlowLine, summaryLine } from "../../components/FlowLine";
import { InvitesCard } from "../../components/InvitesCard";
import { Button } from "../../components/ui/Button";
import { Icon } from "../../components/ui/Icon";
import { Pill } from "../../components/ui/Pill";
import { Toggle } from "../../components/ui/Toggle";
import { useToast } from "../../components/ui/Toast";
import { useAutomationSwitch, useRoomAutomations } from "./useFollowups";
import { useInviteSends } from "./useRoom";

const n = (v: number) => v.toLocaleString("en-US");

function AutoRow({ a, roomId, onSaved }: { a: AutomationSummary; roomId: string; onSaved: (a: AutomationSummary) => void }) {
  const navigate = useNavigate();
  const { on, busy, flip, running, now } = useAutomationSwitch(a, roomId, onSaved);
  const open = () => navigate(`/automations/${a.id}`);
  return (
    <div
      className="autorow"
      role="link"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === "Enter" && e.target === e.currentTarget) open();
      }}
    >
      <div className="autorow-n">
        <div className="nm">{a.name}</div>
        {now ? (
          <div className="ds">
            <b>{n(now)}</b> in it now
          </div>
        ) : null}
      </div>
      <div className="sp autorow-l">
        <FlowLine {...summaryLine(a)} compact />
      </div>
      {running ? (
        <>
          <span className={on ? "small b6 nowrap goodc autorow-s" : "small b6 nowrap muted autorow-s"}>
            {on ? "On for this room" : "Off for this room"}
          </span>
          {/* Why the wrapper: the row opens the automation, the switch only flips it here. */}
          <span onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <Toggle on={on} onChange={flip} label="On or off for this room" disabled={busy} />
          </span>
        </>
      ) : (
        <Pill>Off everywhere</Pill>
      )}
      <Icon name="chev" />
    </div>
  );
}

export function FollowupsTab({ detail }: { detail: WebinarDetail }) {
  const navigate = useNavigate();
  const toast = useToast();
  const { sends, put } = useInviteSends(detail.id);
  const { autos, failed, put: putAuto } = useRoomAutomations(detail.id);
  const live = detail.live;
  const canRemind = !live || (live.series ? !live.series.ended : live.state === "scheduled");

  return (
    <>
      <section className="card">
        <div className="card-h">
          <div>
            <h2>Follow-ups on this room</h2>
          </div>
          <Button size="s" icon="plus" onClick={() => navigate("/automations")}>
            Add a follow-up
          </Button>
        </div>
        <div className="card-b" style={{ paddingTop: 6 }}>
          {autos === null ? (
            <p className="small muted">{failed ? "We couldn't load the follow-ups." : "Loading…"}</p>
          ) : autos.length ? (
            autos.map((a) => (
              <AutoRow key={a.id} a={a} roomId={detail.id} onSaved={putAuto} />
            ))
          ) : (
            <p className="small muted">No follow-ups run on this room yet.</p>
          )}
        </div>
      </section>

      {sends.length ? (
        <div className="mt20">
          <InvitesCard webinarId={detail.id} sends={sends} onChanged={(s: InviteSendView) => put(s)} onToast={toast} canRemind={canRemind} />
        </div>
      ) : null}
    </>
  );
}
